import { Download, FileSpreadsheet, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { downloadBlob, toCsv, toXlsx, XLSX_MIME, type SheetCell } from '@/lib/sheet'
import type { Customer } from '../types'
import { maskPhone, useCrm } from '../ui'

/**
 * Filtrelenmiş müşteri listesini CSV / Excel olarak indirir (dosya tarayıcıda üretilir).
 * Ekrandaki kuralla aynı: telefon maskeli, vergi numarası dışa aktarılmaz.
 */
export function CustomerExportMenu({ rows }: { rows: Customer[] }) {
  const { t } = useTranslation()
  const { staffName, projectName } = useCrm()

  const table = (): SheetCell[][] => {
    const head = ['name', 'legalName', 'sector', 'project', 'stage', 'delivery', 'contact', 'contactTitle', 'phone', 'email', 'city', 'district', 'region', 'owner', 'lastMeeting', 'nextDate', 'nextAction', 'balance', 'website', 'appUrl', 'adminUrl'].map((k) =>
      t(`crm.export.col.${k}`),
    )
    const body = rows.map((c): SheetCell[] => [
      c.name,
      c.legalName,
      c.sector,
      projectName(c.projectId),
      t(`crm.stage.${c.stage}`),
      t(`crm.delivery.${c.delivery}`),
      c.contactName,
      c.contactTitle,
      c.phone ? maskPhone(c.phone) : '',
      c.email,
      c.city,
      c.district,
      c.region,
      staffName(c.ownerId),
      c.lastMeeting ?? '',
      c.nextAction?.date ?? '',
      c.nextAction?.text ?? '',
      Number(c.balance),
      c.website,
      c.appUrl,
      c.adminUrl,
    ])
    return [head, ...body]
  }
  const base = `musteriler-${new Date().toISOString().slice(0, 10)}`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={rows.length === 0}>
          <Download />
          {t('crm.export.button')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuLabel>{t('crm.export.label', { count: rows.length })}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => downloadBlob(`${base}.xlsx`, toXlsx(table(), t('crm.export.sheet')), XLSX_MIME)}>
          <FileSpreadsheet className="text-success" />
          {t('crm.export.xlsx')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => downloadBlob(`${base}.csv`, toCsv(table()), 'text/csv')}>
          <FileText />
          {t('crm.export.csv')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
